import React from 'react';
import { CollaborativeAgreementSigned } from '@uplift/api';
import { useAgreementTemplatePreviewQuery, useCollaboratePhysicianMatchByIdForPhysicianQuery, useStateQuery, useUserDisplayNameQuery } from '@uplift/queries';
import { Table, TableBody, TableRow, Typography } from '@mui/material';
import { AgreementText, DetailsCell, DocumentLayout, EffectiveDateCell, ReviewAndSignAction, useSignatureAnchor } from './components';
import { CounterSignAgreementCard } from './CounterSignAgreementCard';
import { DateFormats } from '@uplift/constants';
import { CalendarDateService } from '@uplift/services';
import { useCurrentuser, useTimezone } from '@uplift/contexts';


export const CollaborativeAgreementCounterSignatureRequiredPreview: React.VFC<{
  document: CollaborativeAgreementSigned;
  onSign: (agreementId: string) => void;
}> = ({
  document,
  onSign,
}) => {
  const me = useCurrentuser();
  const timezone = useTimezone();
  const { data: match } = useCollaboratePhysicianMatchByIdForPhysicianQuery(me.id, document.collaborativeMatchId);
  const { data: preview } = useAgreementTemplatePreviewQuery(document.agreementTemplateId);
  const { data: state } = useStateQuery(match.stateId, { suspense: true });
  const { data: npName } = useUserDisplayNameQuery(match.providerId, { suspense: true });
  const { data: physicianName } = useUserDisplayNameQuery(match.collaborativePhysicianId, { suspense: true });
  const { signatureRef, scrollToSignature } = useSignatureAnchor();

  return (
    <DocumentLayout
      title={preview.template.name}
      subtitle="Sent by BetterHelp"
      action={<ReviewAndSignAction onClick={scrollToSignature} />}
    >
      <Typography variant="overline" color="secondary">AGREEMENT DETAILS</Typography>
      <Table size="small" sx={{ 'tr:last-of-type td': { borderBottom: 0 } }}>
        <TableBody>
          <TableRow>
            <DetailsCell label="State Covered">{state.fullName}</DetailsCell>
            <DetailsCell label="Status">
              {document.counterSignedAt ? 'Active' : 'Awaiting counter-signature'}
            </DetailsCell>
          </TableRow>
          <TableRow>
            <EffectiveDateCell document={document} />
            <DetailsCell label="Expires">{CalendarDateService.date(document.expireAt, DateFormats.ISO_DATE).tz(timezone).format('MMM D, YYYY')}</DetailsCell>
          </TableRow>
          <TableRow>
            <DetailsCell label="Nurse Practitioner">{npName.fullLegalName}</DetailsCell>
            <DetailsCell label="Collaborating Physician">{physicianName.fullLegalName}</DetailsCell>
          </TableRow>
        </TableBody>
      </Table>

      <AgreementText content={preview.version.content} />

      <CounterSignAgreementCard ref={signatureRef} document={document} onSign={onSign} />
    </DocumentLayout>
  );
};
